import { FC } from "react"
import { useNavigate } from "react-router-dom"

import { useTimezone } from "hooks/useTimezone"

import { URL } from "constants/api/requests"

import { ChatMessage } from "types/tournament.types"

interface ChatMessageItemProps {
  message: ChatMessage
  showFilterButton: boolean
  onDelete: (messageId: string) => void
  onRestore: (messageId: string) => void
  onFilter: (playerId: string, playerName: string) => void
}

export const ChatMessageItem: FC<ChatMessageItemProps> = ({ message, showFilterButton, onDelete, onRestore, onFilter }) => {
  const navigate = useNavigate()
  const { formatCustom, formatDateTime } = useTimezone()

  return (
    <div
      className={`group flex items-start justify-between gap-3 rounded-lg px-3 py-2 transition-colors ${
        message.is_deleted ? "bg-rose-50 dark:bg-rose-900/10 opacity-60" : "hover:bg-gray-50 dark:hover:bg-[rgba(139,92,246,0.05)]"
      }`}
    >
      <div className="flex-1 min-w-0 overflow-hidden">
        <div className="flex items-baseline gap-2">
          <button
            onClick={() => navigate(URL.USER.replace(":id", message.player_id))}
            className="text-sm font-medium text-gray-900 dark:text-white truncate hover:text-[rgba(139,92,246,1)] dark:hover:text-[rgba(139,92,246,1)] transition-colors cursor-pointer"
            title="View user profile"
          >
            {message.player_username}
          </button>
          <span className="text-xs text-gray-400 dark:text-gray-500 flex-shrink-0">
            {formatCustom(message.created_at, {
              hour: "2-digit",
              minute: "2-digit",
              second: "2-digit"
            })}
          </span>
          {message.is_system && (
            <span className="text-[10px] font-medium bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded px-1.5 py-0.5">
              SYSTEM
            </span>
          )}
          {message.is_deleted && (
            <span className="text-[10px] font-medium bg-rose-100 dark:bg-rose-900/30 text-rose-700 dark:text-rose-300 rounded px-1.5 py-0.5">
              DELETED
            </span>
          )}
          {message.report_count > 0 && (
            <span className="text-[10px] font-medium bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 rounded px-1.5 py-0.5">
              ⚑ {message.report_count} report{message.report_count !== 1 ? "s" : ""}
            </span>
          )}
          {showFilterButton && (
            <button
              onClick={() => onFilter(message.player_id, message.player_username)}
              className="px-1.5 py-0.5 text-[10px] font-medium rounded bg-gray-100 dark:bg-[rgba(255,255,255,0.06)] text-gray-500 dark:text-gray-400 hover:bg-violet-100 hover:text-violet-600 dark:hover:bg-violet-900/30 dark:hover:text-violet-400 border border-transparent hover:border-violet-300 dark:hover:border-violet-700 transition-all opacity-0 group-hover:opacity-100"
              title={`Filter messages by ${message.player_username}`}
            >
              ⊕ Filter messages by this user
            </button>
          )}
        </div>
        <p
          className={`text-sm mt-0.5 break-all overflow-hidden ${
            message.is_deleted ? "text-gray-400 dark:text-gray-500 line-through" : "text-gray-700 dark:text-gray-300"
          }`}
        >
          {message.message}
        </p>
        {message.is_deleted && message.deleted_at && (
          <p className="text-[10px] text-rose-500 dark:text-rose-400 mt-0.5">Deleted at {formatDateTime(message.deleted_at)}</p>
        )}
      </div>

      {/* Admin Actions */}
      <div className="flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-1">
        {message.is_deleted ? (
          <button
            onClick={() => onRestore(message.id)}
            className="px-2 py-1 text-xs border border-emerald-600/40 text-white rounded hover:bg-emerald-700/20 transition-colors"
            title="Restore message"
          >
            Restore
          </button>
        ) : (
          <button
            onClick={() => onDelete(message.id)}
            className="px-2 py-1 text-xs border border-rose-600/40 text-white rounded hover:bg-rose-700/20 transition-colors"
            title="Delete message"
          >
            Delete
          </button>
        )}
      </div>
    </div>
  )
}
